// @givmo/app-admin — the shared match-approval engine (queries + Owner gate + the two dialogs).
//
// TWO surfaces drive the SAME money path since the approvals split (EP-DES1 §1): `/approvals` (the
// full queue) and the dashboard's Needs-attention summary. This hook is what keeps that from becoming
// a clone — it owns every read, every derivation, the Owner gate, the decline mutation, and it RENDERS
// the two dialogs, so both surfaces import <ApproveMatchDialog> and <ConfirmDialog> exactly once, from
// here. (The Gift Cards surface was built by cloning Give's flow and the clone drifted into shipping a
// wrong "$0 fees" line — EP-FIX3. Cloning a charge surface is the precedent this file exists to avoid.)
//
// ★ IT OWNS NO CHARGE LOGIC. Every charge-safety guard — idempotency-key minting, no-blind-retry on a
// maybe-charged error, single-flight, confirm-before-charge, card-required, budget pre-check — lives
// inside <ApproveMatchDialog> and is untouched by the split. This hook decides only WHICH target the
// dialog opens with and what happens after a decision lands (invalidate + toast).
//
// Reads (all five ride the generated *Options factories, so the two surfaces share one cache):
//   - companiesGetCompanyDashboard      → the activity feed the approval rows decode from
//   - companiesGetCompanyAdmin          → the Owner gate over approve/decline
//   - companiesGetCompanyCardsV2        → the vaulted funding cards fed to the charge dialog
//   - companiesGetCompanyWallet         → the wallet funding option
//   - companiesGetCompany               → the company's name, the donor of record the charge
//                                         dialog's summary names (D-CAM-41)
//
// OWNER GATE (mirrors Settings): role from GET /admin/company/me; a Member sees rows READ-ONLY (no
// action buttons, no Approve-all); a role-read ERROR FAILS OPEN to editable — the backend
// permission-checks every write regardless, so a transient failure must never strand a real Owner.
//
// ROW MODEL (07-CUT0 ①): a grouped (ETF/fundraiser) request is ONE row keyed by its `request_id`
// (EP-WEB38), and its Approve/Decline drive the GROUPED routes over the complete member set (the
// single routes 400 on a grouped post).

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  companiesGetCompanyAdminOptions,
  companiesGetCompanyCardsV2Options,
  companiesGetCompanyDashboardOptions,
  companiesGetCompanyOptions,
  companiesGetCompanyDashboardQueryKey,
  companiesGetCompanyWalletOptions,
  companiesGetCompanyWalletQueryKey,
} from '@givmo/api-client/query';
import { isGivmoError, sdk } from '@givmo/api-client';
import { toast } from '@givmo/design-system';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { extractCards } from '../settings/payment-methods-decode';
import { ROLE_OWNER } from '../admins/admins-roles';
import { ApproveMatchDialog, type ApproveTarget } from '../dashboard/approve-match-dialog';
import {
  charityCountLabel,
  employeeInSentence,
  groupPendingRows,
  groupRowTitle,
  isActivityTruncated,
  pendingLeftOutOfApproveAll,
  toApprovalRows,
  totalApprovableAmount,
  toWalletFunding,
  waitingRequests,
  type ApprovalGroup,
  type MatchApprovalRow,
} from '../dashboard/dashboard-decode';

export interface MatchApprovals {
  /** Every approval row the feed carries (pending first, as the backend sorts them). */
  rows: MatchApprovalRow[];
  /** The pending subset's count — the "N waiting" badge and the nav badge. */
  pendingCount: number;
  /**
   * Approve all's units over the pending rows (singles + grouped-post units), oldest request first,
   * each at its row's figure (EP-BE19).
   */
  groups: ApprovalGroup[];
  /** Approve all's total (the units' exact-cent sum), or null when there is nothing to approve. */
  approvalTotal: number | null;
  /** Waiting requests Approve all leaves out: nothing would be left for them by their turn. */
  leftOutOfApproveAll: number;
  /**
   * Owner (or a failed/pending role read) — the FAIL-OPEN gate over the approve/decline CONTROLS. A
   * transient role-read failure must never strand a real Owner, and the backend permission-checks
   * every write regardless.
   */
  canManage: boolean;
  /**
   * Owner CONFIRMED by a resolved role read — the strict gate for anything that must not happen on a
   * guess. Use this (never `canManage`) to enable an Owner-only READ: `canManage` is true while the
   * role is still pending, so gating a fetch on it fires a request that 403s for a Member.
   */
  isOwnerConfirmed: boolean;
  /** Approve-all is live only for an Owner with at least one PRICEABLE pending group. */
  canApproveAll: boolean;
  /** The row whose decline is in flight (its buttons disable); null when idle. */
  rejectingId: string | null;
  /** Whether the single-page activity feed was CUT at the backend page boundary. */
  truncated: boolean;
  /**
   * When the list was last read (the dashboard query's `dataUpdatedAt`). An opened collection row
   * reads its shares no earlier, so a refreshed list reads them afresh (EP-WEB40; cut 31's review F1).
   */
  listLoadedAt: number;
  loading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  /** Open the charge dialog for one row (single or grouped). */
  openApprove: (row: MatchApprovalRow) => void;
  /** Open the charge dialog over every pending group. */
  openApproveAll: () => void;
  /** Open the non-charging decline confirm for one row. */
  openDecline: (row: MatchApprovalRow) => void;
  /**
   * The two dialogs, already wired. A surface MUST render this once — without it its Approve/Decline
   * buttons open nothing.
   */
  dialogs: ReactNode;
}

export function useMatchApprovals(): MatchApprovals {
  const queryClient = useQueryClient();
  const dashboard = useQuery(companiesGetCompanyDashboardOptions());

  const roleQuery = useQuery(companiesGetCompanyAdminOptions());
  const canManage = !(roleQuery.isSuccess && roleQuery.data?.admin_company_role !== ROLE_OWNER);
  const isOwnerConfirmed = roleQuery.isSuccess && roleQuery.data?.admin_company_role === ROLE_OWNER;

  // The vaulted funding cards (07-1.3), fed to the charge dialog's funding selector.
  const cardsQuery = useQuery(companiesGetCompanyCardsV2Options());
  const cards = useMemo(() => extractCards(cardsQuery.data), [cardsQuery.data]);

  // The company wallet — an ADDITIONAL funding source for match approval (the card path is unchanged).
  // Read-only + tolerant: a pending/failed read → `walletFunding` null → the dialog stays card-only.
  const walletQuery = useQuery(companiesGetCompanyWalletOptions());
  const walletFunding = useMemo(() => toWalletFunding(walletQuery.data), [walletQuery.data]);

  // The donor of record the charge dialog's summary names (D-CAM-41). Tolerant: unknown → the
  // dialog says "Your company".
  const companyQuery = useQuery(companiesGetCompanyOptions());
  const companyName = companyQuery.data?.name?.trim() || null;

  const data = dashboard.data;
  // Memoized on the cached payload: `toApprovalRows` mints fresh row objects, so decoding inline
  // would hand the table a new identity for every row on every render and defeat the row memo.
  const rows = useMemo(() => toApprovalRows(data), [data]);
  const pendingCount = rows.filter((r) => r.status === 'Pending').length;

  const groups = useMemo(() => groupPendingRows(rows), [rows]);
  // Every waiting request, oldest first — what Approve all re-prices at the figures now (EP-WEB41).
  const waiting = useMemo(() => waitingRequests(rows), [rows]);
  const approvalTotal = useMemo(
    () => (groups.length > 0 ? totalApprovableAmount(groups) : null),
    [groups],
  );
  const leftOutOfApproveAll = useMemo(() => pendingLeftOutOfApproveAll(rows), [rows]);
  const canApproveAll = canManage && groups.length > 0 && approvalTotal !== null;

  // ── Charge dialog (Approve / Approve-all) ──
  const [approveTarget, setApproveTarget] = useState<ApproveTarget | null>(null);

  const invalidateDashboard = () =>
    queryClient.invalidateQueries({ queryKey: companiesGetCompanyDashboardQueryKey() });

  const onApproved = () => {
    setApproveTarget(null);
    void invalidateDashboard();
    toast.success('Match approved');
  };

  // The charge dialog closed after a charge attempt WITHOUT a confirmed success (a fatal error, or an
  // acknowledged maybe-charged close — EP-FIX2). Refresh the money reads so the pending approvals + the
  // balance the user is told to check are fresh truth (a maybe-charged approve may have actually
  // landed). No toast, no success signal — the dialog's own onOpenChange clears the target.
  const onApproveCloseAfterAttempt = () => {
    void invalidateDashboard();
    void queryClient.invalidateQueries({ queryKey: companiesGetCompanyWalletQueryKey() });
  };

  // Stable identity: this reaches the table's column builder, whose identity in turn gates the row
  // memo. It closes over nothing but the state setter.
  const openApprove = useCallback((row: MatchApprovalRow) => {
    if (row.amountValue === null) return; // non-chargeable — Approve is disabled anyway.
    // The dialog opens on the row's match (EP-BE19's figure — never the employee's gift, D-EP-56).
    if (row.kind === 'grouped') {
      // ONE grouped charge over the complete member set — the single routes 400 on grouped posts.
      setApproveTarget({
        kind: 'grouped',
        matchIds: row.matchIds,
        amount: row.amountValue,
        groupName: groupRowTitle(row),
        charityCount: row.members.length,
        kindLabel: row.groupKindLabel,
        employeeName: employeeInSentence(row),
        collection: row.collection,
        members: row.members,
      });
      return;
    }
    setApproveTarget({
      kind: 'single',
      matchId: row.id,
      amount: row.amountValue,
      charityName: row.charity.name,
      logoUrl: row.charity.logo_url ?? null,
      employeeName: employeeInSentence(row),
    });
  }, []);

  const openApproveAll = () => {
    if (!canApproveAll || approvalTotal === null) return;
    setApproveTarget({
      kind: 'all',
      groups,
      amount: approvalTotal,
      leftOut: leftOutOfApproveAll,
      requests: waiting,
    });
  };

  // ── Decline (non-charging reject) ──
  const [declineRow, setDeclineRow] = useState<MatchApprovalRow | null>(null);
  // Stable for the same reason as `openApprove` — it feeds the column builder.
  const openDecline = useCallback((row: MatchApprovalRow) => setDeclineRow(row), []);
  const rejectMutation = useMutation({
    // A grouped row declines through the grouped route with its COMPLETE member set (the single
    // reject 400s on grouped posts, and the backend requires the whole batch).
    mutationFn: (row: MatchApprovalRow) =>
      row.kind === 'grouped'
        ? sdk.donationMatchesRejectGroupedCompanyMatches({
            body: { match_ids: row.matchIds },
            throwOnError: true,
          })
        : sdk.donationMatchesRejectCompanyMatch({ path: { match_id: row.id }, throwOnError: true }),
    onSuccess: async () => {
      setDeclineRow(null);
      await invalidateDashboard();
      toast.success('Match declined');
    },
    onError: (error: unknown) => {
      toast.error(
        isGivmoError(error) ? error.message : 'Could not decline the match. Please try again.',
      );
    },
  });

  const dialogs = (
    <>
      {/* ★ MONEY-MOVING approve/charge dialog (single row, grouped row, OR Approve-all). Owns the
          charge-safety guards; this hook hands it the target + vaulted cards and owns success. */}
      <ApproveMatchDialog
        open={approveTarget !== null}
        onOpenChange={(open) => {
          if (!open) setApproveTarget(null);
        }}
        target={approveTarget}
        cards={cards}
        walletFunding={walletFunding}
        companyName={companyName}
        onApproved={onApproved}
        onCloseAfterAttempt={onApproveCloseAfterAttempt}
      />

      {/* Decline (non-charging reject) confirm. */}
      <ConfirmDialog
        open={declineRow !== null}
        onOpenChange={(open) => {
          if (!open && !rejectMutation.isPending) setDeclineRow(null);
        }}
        title="Decline this match?"
        subject={
          declineRow !== null
            ? `${declineRow.employeeName} → ${
                declineRow.kind === 'grouped' ? groupRowTitle(declineRow) : declineRow.charity.name
              }`
            : undefined
        }
        body={
          declineRow?.kind === 'grouped'
            ? `This grouped match request (${charityCountLabel(declineRow.members.length)}) will be declined. No card is charged. This cannot be undone.`
            : 'This match request will be declined. No card is charged. This cannot be undone.'
        }
        confirmLabel="Decline match"
        destructive
        loading={rejectMutation.isPending}
        onConfirm={() => {
          if (declineRow !== null) rejectMutation.mutate(declineRow);
        }}
        testId="dashboard-decline-confirm"
      />
    </>
  );

  return {
    rows,
    pendingCount,
    groups,
    approvalTotal,
    leftOutOfApproveAll,
    canManage,
    isOwnerConfirmed,
    canApproveAll,
    rejectingId: rejectMutation.isPending ? (declineRow?.id ?? null) : null,
    truncated: isActivityTruncated(data),
    listLoadedAt: dashboard.dataUpdatedAt,
    loading: dashboard.isPending,
    isError: dashboard.isError,
    error: dashboard.error,
    refetch: () => void dashboard.refetch(),
    openApprove,
    openApproveAll,
    openDecline,
    dialogs,
  };
}
